import {
  BiDisplayFormat,
  BiDisplayFormatKind,
  formatBiCell,
  formatBiValue,
} from '@euler/model/bi/format';
import { BiCell, BiFieldRole, BiQueryResult } from '@euler/model/bi/result';
import { BiChartViz, BiTableViz } from '@euler/model/bi/viz';
import { ColumnAlign } from '../../../markdown/MarkdownTable';
import { BiColumnRef, biColumnLookup } from './biColumns';
import { biCategoryLabel, biPivot } from './biPivot';
import { biText } from './biText';

/** A result as the rows of a table, every cell already in words. */
export interface BiTableModel {
  header: string[];
  /**
   * The header a copy carries, when it differs: a pivot's series columns
   * are values, copied as stored like the rows.
   */
  rawHeader?: string[];
  /** Every row; the view decides how many it shows. */
  body: string[][];
  /** The same cells as stored — what a copy of the table carries. */
  raw: BiCell[][];
  align: ColumnAlign[];
  /** The totals row: `合计`, then each additive column's sum or blank. */
  totals: string[];
}

const kTotalLabel = '合计';

/**
 * Whether a measure's cells add up to a meaningful total: not a percent,
 * an average, a minimum, a maximum or a distinct count.
 */
function additive(key: string, format: BiDisplayFormat | undefined): boolean {
  if (format?.kind === BiDisplayFormatKind.Percent) return false;
  return !/__(avg|min|max|count_distinct)$/.test(key);
}

/** The sum of a column's cells as text, or blank when it has none. */
function totalOf(cells: BiCell[], format: BiDisplayFormat | undefined): string {
  let sum = 0;
  let any = false;
  for (const cell of cells) {
    const value = typeof cell === 'boolean' ? NaN : Number(cell);
    if (cell === null || cell === '' || !Number.isFinite(value)) continue;
    sum += value;
    any = true;
  }
  return any ? formatBiValue(sum, format) : '';
}

function totalsOf(result: BiQueryResult, columns: BiColumnRef[]): string[] {
  return columns.map((ref, i) => {
    if (ref.column.role !== BiFieldRole.Measure)
      return i === 0 ? kTotalLabel : '';
    if (!additive(ref.column.key, ref.format)) return '';
    return totalOf(
      result.rows.map(row => row[ref.index]),
      ref.format,
    );
  });
}

const alignOf = (ref: BiColumnRef): ColumnAlign =>
  ref.column.role === BiFieldRole.Measure ? 'right' : 'left';

function plainTable(
  result: BiQueryResult,
  columns: BiColumnRef[],
  names: (string | undefined)[],
): BiTableModel {
  return {
    header: columns.map((ref, i) => names[i] ?? biText(ref.column.label)),
    body: result.rows.map(row =>
      columns.map(ref =>
        formatBiCell(row[ref.index], ref.format, ref.column.valueLabels),
      ),
    ),
    raw: result.rows.map(row => columns.map(ref => row[ref.index])),
    align: columns.map(alignOf),
    totals: totalsOf(result, columns),
  };
}

/** A table viz's columns, in its order and under its names. */
export function biTableOfTableViz(
  viz: BiTableViz,
  result: BiQueryResult,
): BiTableModel | null {
  const column = biColumnLookup(result, viz.formats);
  const refs = viz.columns.map(entry => column(entry.key));
  if (refs.length === 0 || refs.some(ref => ref === null)) return null;
  return plainTable(
    result,
    refs as BiColumnRef[],
    viz.columns.map(entry => entry.name),
  );
}

/**
 * The result behind a chart, as the chart draws it: every dimension and
 * measure column under the chart's names and formats or, for a `seriesBy`
 * chart, the category then one column per series.
 */
export function biTableOfChart(
  viz: BiChartViz,
  result: BiQueryResult,
): BiTableModel | null {
  const column = biColumnLookup(result, viz.formats);
  const x = column(viz.x);
  const measures = viz.series.map(entry => column(entry.key));
  if (!x || measures.length === 0 || measures.some(ref => ref === null))
    return null;
  if (viz.seriesBy) {
    const by = column(viz.seriesBy);
    const measure = measures[0];
    if (!by || !measure) return null;
    const { groups, names, rows } = biPivot(result, x, by, measure);
    return {
      header: [
        biText(x.column.label),
        ...groups.map(group => names.get(group) ?? group),
      ],
      rawHeader: [biText(x.column.label), ...groups],
      body: rows.map(row => [
        row.category,
        ...groups.map(group =>
          formatBiValue(row.cells.get(group) ?? null, measure.format),
        ),
      ]),
      raw: rows.map(row => [
        row.value,
        ...groups.map(group => row.cells.get(group) ?? null),
      ]),
      align: ['left', ...groups.map((): ColumnAlign => 'right')],
      totals: [
        kTotalLabel,
        ...groups.map(group =>
          additive(measure.column.key, measure.format)
            ? totalOf(
                rows.map(row => row.cells.get(group) ?? null),
                measure.format,
              )
            : '',
        ),
      ],
    };
  }
  // The chart's order first (category, then series in legend order), then
  // whatever else the query returns.
  const names = new Map(viz.series.map(entry => [entry.key, entry.name]));
  const charted = [viz.x, ...viz.series.map(entry => entry.key)];
  const keys = [
    ...charted,
    ...result.columns
      .map(entry => entry.key)
      .filter(key => !charted.includes(key)),
  ];
  const refs = keys.map(key => column(key)!);
  const table = plainTable(
    result,
    refs,
    refs.map(ref => names.get(ref.column.key)),
  );
  // The category reads as the chart labels it, `(空)` included.
  return {
    ...table,
    body: table.body.map((row, i) => [
      biCategoryLabel(result.rows[i][x.index], x.column.valueLabels),
      ...row.slice(1),
    ]),
  };
}

/**
 * A table as tab-separated text for the clipboard: its header, then every
 * row's cells as stored — a rate stays 0.5, money stays in cents — so a
 * spreadsheet gets the values, not their display.
 */
export function biTableTsv(table: BiTableModel): string {
  const cell = (value: BiCell | string) =>
    value === null ? '' : String(value).replace(/[\t\r\n]+/g, ' ');
  return [table.rawHeader ?? table.header, ...table.raw]
    .map(row => row.map(cell).join('\t'))
    .join('\n');
}
